import { Observable, catchError, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { ReportHistoryRow, ReportSummaryRow } from '../../interfaces/reporteria.interface'
import { environment } from '@env/environment'

type Wrapped<T> = { error: boolean; msg: string; data: T | null }

interface HistoryPage {
  data: ReportHistoryRow[]
  totalItems: number
  page: number
  itemsByPage: number
}

/**
 * Lo que Reportería necesita de la API.
 *
 * Los tres endpoints cuelgan de `wallet/config` —donde ya vivía el historial de los cortes— y no de
 * un `reports` propio: el módulo `reports` de la API se retiró con los volcados genéricos que nadie
 * usaba, y levantar uno nuevo para tres lecturas del mismo historial sería un módulo por pantalla.
 */
@Injectable({ providedIn: 'root' })
export class ApiReportsService {
  private readonly http = inject(HttpClient)
  private readonly base = `${environment.apiUrl}wallet/config`

  /** El pulso por tipo de reporte: cuántos y quién sacó el último. */
  resumen(): Observable<ReportSummaryRow[]> {
    return this.http.get(`${this.base}/reportes/resumen`).pipe(
      map((r) => (r as Wrapped<ReportSummaryRow[]>).data ?? []),
      catchError(() => of([] as ReportSummaryRow[])),
    )
  }

  /** Una página del historial. `filters` sigue la forma `{ campo: { value, matchMode } }`. */
  historial(page: number, limit: number, filters: Record<string, any> = {}): Observable<HistoryPage | null> {
    return this.http.post(`${this.base}/history`, { page, limit, filters, sort: { generationDate: -1 } }).pipe(
      map((r) => (r as Wrapped<HistoryPage>).data),
      catchError(() => of(null)),
    )
  }

  /**
   * Vuelve a bajar el archivo de un reporte del historial.
   *
   *! `null` en el error en vez de propagar: la pantalla enseña un aviso y sigue viva. El archivo
   *! puede no estar —los reportes anteriores a este cambio no se guardaban— y eso no es una falla
   *! que deba tumbar la tabla.
   */
  descargar(id: string): Observable<Blob | null> {
    return this.http.get(`${this.base}/historial/${id}/download`, { responseType: 'blob' }).pipe(catchError(() => of(null)))
  }
}
